import { useCallback, useEffect, useRef, useState } from 'react'
import { SIMPLE_COPY } from '@proffer/shared'
import { apiPostMultipart } from '../config/api'

type TranscribeResponse = { text: string }

function pickRecorderMime(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined
  const list = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ]
  for (const t of list) {
    if (MediaRecorder.isTypeSupported(t)) return t
  }
  return undefined
}

function extensionForBlob(blob: Blob): string {
  const t = blob.type.toLowerCase()
  if (t.includes('webm')) return 'webm'
  if (t.includes('mp4')) return 'mp4'
  if (t.includes('mpeg') || t.includes('mp3')) return 'mp3'
  if (t.includes('ogg')) return 'ogg'
  if (t.includes('wav')) return 'wav'
  return 'webm'
}

function MicIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" x2="12" y1="19" y2="22" />
      <line x1="8" x2="16" y1="22" y2="22" />
    </svg>
  )
}

function voiceRecordingBlockReason():
  | null
  | 'no-mediarecorder'
  | 'insecure-or-blocked-mediadevices' {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return 'insecure-or-blocked-mediadevices'
  }
  if (typeof MediaRecorder === 'undefined') {
    return 'no-mediarecorder'
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    return 'insecure-or-blocked-mediadevices'
  }
  return null
}

export type VoiceNotesRecorderProps = {
  onTranscribed: (text: string) => void
  disabled?: boolean
}

export function VoiceNotesRecorder({ onTranscribed, disabled }: VoiceNotesRecorderProps) {
  const [isRecording, setIsRecording] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])

  const blockReason = voiceRecordingBlockReason()
  const supported = blockReason === null

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop()
      }
      streamRef.current?.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
  }, [])

  const sendBlob = useCallback(
    async (blob: Blob) => {
      setError(null)
      setIsSending(true)
      try {
        const ext = extensionForBlob(blob)
        const fd = new FormData()
        fd.append('file', blob, `voice.${ext}`)
        const { text } = await apiPostMultipart<TranscribeResponse>(
          '/audio/transcribe',
          fd,
        )
        if (text?.trim()) {
          onTranscribed(text.trim())
        }
      } catch (e) {
        setError(
          e instanceof Error
            ? e.message
            : 'Η απομαγνητοφώνηση απέτυχε. Δοκίμασε ξανά.',
        )
      } finally {
        setIsSending(false)
      }
    },
    [onTranscribed],
  )

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
  }, [])

  const toggleRecording = useCallback(async () => {
    if (!supported || disabled || isSending) return
    setError(null)

    if (isRecording) {
      const mr = mediaRecorderRef.current
      if (mr && mr.state === 'recording') {
        mr.stop()
      }
      setIsRecording(false)
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
        },
      })
      streamRef.current = stream
      chunksRef.current = []
      const mime = pickRecorderMime()
      const mr = new MediaRecorder(
        stream,
        mime ? { mimeType: mime } : undefined,
      )
      mediaRecorderRef.current = mr
      mr.ondataavailable = (ev) => {
        if (ev.data.size > 0) chunksRef.current.push(ev.data)
      }
      mr.onstop = () => {
        stopStream()
        const parts = chunksRef.current
        chunksRef.current = []
        const blob = new Blob(parts, {
          type: mr.mimeType || 'audio/webm',
        })
        if (blob.size > 0) {
          void sendBlob(blob)
        }
      }
      mr.start(250)
      setIsRecording(true)
    } catch {
      stopStream()
      setError(
        'Δεν δόθηκε πρόσβαση στο μικρόφωνο ή το πρόγραμμα περιήγησης δεν υποστηρίζει ηχογράφηση.',
      )
    }
  }, [
    disabled,
    isRecording,
    isSending,
    sendBlob,
    stopStream,
    supported,
  ])

  if (blockReason === 'no-mediarecorder') {
    return (
      <p className="max-w-xs text-xs text-zinc-500 dark:text-zinc-500 sm:text-right">
        Ο browser δεν έχει{' '}
        <code className="rounded bg-zinc-200/80 px-1 dark:bg-zinc-800">MediaRecorder</code>
        . Δοκίμασε τελευταίο Chrome, Edge ή Firefox.
      </p>
    )
  }

  if (blockReason === 'insecure-or-blocked-mediadevices') {
    return (
      <p className="max-w-xs text-right text-xs font-medium leading-snug text-amber-900 dark:text-amber-200 sm:text-right">
        Η φωνητική καταγραφή δεν λειτουργεί από αυτή τη διεύθυνση.
      </p>
    )
  }

  return (
    <div className="flex flex-col items-center gap-2 sm:items-end">
      <button
        type="button"
        onClick={() => void toggleRecording()}
        disabled={disabled || isSending}
        aria-pressed={isRecording}
        aria-label={
          isRecording
            ? 'Σταμάτημα ηχογράφησης'
            : 'Έναρξη ηχογράφησης φωνής'
        }
        className={`flex h-20 w-20 shrink-0 flex-col items-center justify-center rounded-full shadow-lg transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-500 disabled:cursor-not-allowed disabled:opacity-50 sm:h-24 sm:w-24 ${
          isRecording
            ? 'animate-pulse bg-red-600 text-white ring-4 ring-red-400/50 hover:bg-red-500'
            : 'bg-gradient-to-br from-violet-600 to-indigo-600 text-white hover:from-violet-500 hover:to-indigo-500'
        }`}
      >
        <MicIcon className="h-9 w-9 sm:h-10 sm:w-10" />
        <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide opacity-90">
          {isSending
            ? '…'
            : isRecording
              ? 'STOP'
              : SIMPLE_COPY.speakCta}
        </span>
      </button>
      <p className="max-w-[220px] text-center text-xs leading-snug text-zinc-600 dark:text-zinc-400 sm:text-right">
        {isSending
          ? 'Μετατροπή φωνής σε κείμενο…'
          : isRecording
            ? 'Πάτα STOP όταν τελειώσεις.'
            : 'Πάτα για ηχογράφηση· ξανά για σταμάτημα.'}
      </p>
      {error && (
        <p
          className="max-w-[260px] text-center text-xs text-red-700 dark:text-red-300 sm:text-right"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  )
}
